import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import Button from "../../../components/ui/Button/Button";
import { adminApi } from "../../../lib/admin";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { formatDateTime } from "../../../utils/DateUtils";

const AdminBusinessVolunteerApprovalPage = () => {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const requestId = searchParams.get("requestId")?.trim() ?? "";
  const token = searchParams.get("token")?.trim() ?? "";
  const hasValidParams = requestId.length > 0 && token.length > 0;

  const reviewQuery = useQuery({
    queryKey: ["admin-business-volunteer-approval-review", requestId, token],
    queryFn: () => adminApi.getBusinessVolunteerApprovalReview(requestId, token),
    enabled: hasValidParams,
  });

  const approveMutation = useMutation({
    mutationFn: () => adminApi.approveBusinessVolunteerApproval(requestId, { token }),
    onSuccess: async (review) => {
      queryClient.setQueryData(["admin-business-volunteer-approval-review", requestId, token], review);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () => adminApi.rejectBusinessVolunteerApproval(requestId, { token }),
    onSuccess: async (review) => {
      queryClient.setQueryData(["admin-business-volunteer-approval-review", requestId, token], review);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["admin-users"] }),
        queryClient.invalidateQueries({ queryKey: ["admin-audit"] }),
      ]);
    },
  });

  const review = approveMutation.data ?? rejectMutation.data ?? reviewQuery.data;
  const mutationError = approveMutation.error ?? rejectMutation.error;
  const errorMessage = !hasValidParams
    ? "This approval link is missing required information."
    : reviewQuery.error
      ? apiErrorToMessage(reviewQuery.error, "Unable to load the volunteer approval request.")
      : mutationError
        ? apiErrorToMessage(mutationError, "Unable to save this volunteer approval decision.")
        : null;
  const isPending = review?.status === "PENDING_APPROVAL";
  const isSavingDecision = approveMutation.isPending || rejectMutation.isPending;

  return (
    <main className="mx-auto max-w-3xl px-6 py-10">
      <div className="rounded-3xl border border-myteengig-border bg-white shadow-sm">
        <div className="border-b border-myteengig-border px-8 py-8">
          <p className="text-sm font-semibold uppercase tracking-[0.22em] text-myteengig-primary">Volunteer Posting Approval</p>
          <h1 className="mt-3 text-3xl font-bold text-myteengig-text-primary">
            {isPending ? "Review nonprofit request" : "Volunteer request decided"}
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-myteengig-text-muted">
            Approval lets this business account publish volunteer listings without the business listing fee.
          </p>
        </div>

        <div className="space-y-6 px-8 py-8">
          {reviewQuery.isLoading ? <p className="text-sm text-myteengig-text-muted">Loading volunteer approval request...</p> : null}
          {errorMessage ? <p className="text-sm font-medium text-red-600">{errorMessage}</p> : null}

          {review ? (
            <>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Request</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{review.status}</p>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Account</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{review.currentApprovalStatus}</p>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-myteengig-text-muted">Requested</p>
                  <p className="mt-3 text-sm font-semibold text-myteengig-text-primary">{formatDateTime(review.requestedAt)}</p>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Business</p>
                  <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                    <p>{review.businessName ?? "No business name"}</p>
                    <p>{review.businessEmail}</p>
                    <p>{review.businessWebsite ?? "No website provided"}</p>
                  </div>
                </div>
                <div className="rounded-2xl border border-myteengig-border p-4">
                  <p className="text-sm font-semibold text-myteengig-text-primary">Contact</p>
                  <div className="mt-3 space-y-2 text-sm text-myteengig-text-muted">
                    <p>{review.businessContactName}</p>
                    <p>User ID: {review.businessUserId}</p>
                    {review.decidedAt ? <p>Decided: {formatDateTime(review.decidedAt)}</p> : null}
                    {review.decidedByAdminEmail ? <p>By: {review.decidedByAdminEmail}</p> : null}
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-myteengig-border p-4">
                <p className="text-sm font-semibold text-myteengig-text-primary">Business description</p>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-myteengig-text-muted">
                  {review.businessDescription ?? "No description provided."}
                </p>
              </div>

              <div className="flex flex-wrap items-center justify-end gap-3">
                <Link
                  to="/admin"
                  className="inline-flex items-center rounded-md border border-myteengig-border px-4 py-2 text-sm font-semibold text-myteengig-text-primary transition hover:bg-myteengig-background"
                >
                  Back to admin
                </Link>
                <Button
                  type="button"
                  variant="outline"
                  disabled={!hasValidParams || !isPending || isSavingDecision}
                  onClick={() => rejectMutation.mutate()}
                >
                  {rejectMutation.isPending ? "Rejecting..." : review.status === "REJECTED" ? "Rejected" : "Reject"}
                </Button>
                <Button
                  type="button"
                  disabled={!hasValidParams || !isPending || isSavingDecision}
                  onClick={() => approveMutation.mutate()}
                >
                  {approveMutation.isPending ? "Approving..." : review.status === "APPROVED" ? "Approved" : "Approve"}
                </Button>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </main>
  );
};

export default AdminBusinessVolunteerApprovalPage;
